/**
 * Authentication Utilities
 *
 * Provides token management utilities without circular dependencies.
 * Used by HTTP interceptors and services.
 */

import type { User } from "../types/api/user-types";
import type { TokenResponse } from "../types/api/auth-types";

/**
 * Get current access token from localStorage
 * @returns string | null - The access token or null if not found
 */
export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return localStorage.getItem("auth_access_token");
  } catch (error) {
    console.error("Error accessing localStorage for token:", error);
    return null;
  }
}

/**
 * Check if user is currently authenticated
 * @returns boolean - True if authenticated, false otherwise
 */
export function isAuthenticated(): boolean {
  const token = getAccessToken();
  if (!token) return false;

  // Check if token is expired
  try {
    const expiry = localStorage.getItem("auth_token_expiry");
    if (expiry) {
      const expiryDate = new Date(expiry);
      if (expiryDate <= new Date()) {
        // Token is expired, clean up
        clearAuthData();
        return false;
      }
    }
  } catch (error) {
    console.error("Error checking token expiry:", error);
  }

  return true;
}

/**
 * Get current user from localStorage
 * @returns User | null - The current user or null if not found
 */
export function getCurrentUser(): User | null {
  if (typeof window === "undefined") return null;

  try {
    const userStr = localStorage.getItem("auth_user");
    if (!userStr) return null;

    return JSON.parse(userStr) as User;
  } catch (error) {
    console.error("Error accessing localStorage for user:", error);
    return null;
  }
}

/**
 * Clear all authentication data from localStorage
 */
export function clearAuthData(): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem("auth_access_token");
    localStorage.removeItem("auth_refresh_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("auth_token_expiry");
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
}

/**
 * Store authentication data in localStorage
 * @param tokenResponse - Token response from login/register
 * @param user - User object
 */
export function storeAuthData(tokenResponse: TokenResponse, user: User): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem("auth_access_token", tokenResponse.access_token);
    localStorage.setItem("auth_user", JSON.stringify(user));

    // Calculate expiry date if expires_in is provided
    if (tokenResponse.expires_in) {
      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + tokenResponse.expires_in);
      localStorage.setItem("auth_token_expiry", expiryDate.toISOString());
    }

    if (tokenResponse.refresh_token) {
      localStorage.setItem("auth_refresh_token", tokenResponse.refresh_token);
    }
  } catch (error) {
    console.error("Error storing auth data:", error);
  }
}
